import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CouponApi,
  CouponEarnRedeemTransactionRequest,
  VerifyCouponRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { transactionFormSchema, TransactionFormValues } from "./types";

export function CouponsTransaction() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCouponVerified, setIsCouponVerified] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      invoiceNo: "",
      invoiceAmount: "",
      mobileNo: "",
      isCouponRedeem: false,
      couponCode: "",
      otp: "",
    },
  });

  const isCouponRedeem = form.watch("isCouponRedeem");

  // Reset verification states when checkbox is unchecked
  React.useEffect(() => {
    if (!isCouponRedeem) {
      setIsCouponVerified(false);
      setIsOtpVerified(false);
      form.setValue("couponCode", "");
      form.setValue("otp", "");
    }
  }, [isCouponRedeem, form]);

  const handleVerifyCoupon = async () => {
    const couponCode = form.getValues("couponCode");
    const mobileNo = form.getValues("mobileNo");

    if (!couponCode || !couponCode.trim()) {
      toast({
        title: "Error",
        description: "Please enter a coupon code",
        variant: "destructive",
      });
      return;
    }

    if (!mobileNo || !mobileNo.trim()) {
      toast({
        title: "Error",
        description: "Please enter mobile number first",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      const payload: VerifyCouponRequest = {
        p_mobileNo: mobileNo,
        p_couponCode: couponCode,
      };

      const response = await CouponApi.verifyCoupon(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Verification Failed",
          description: response.error_msg,
          variant: "destructive",
        });
        setIsCouponVerified(false);
        setIsOtpVerified(false);
      } else {
        toast({
          title: "Success",
          description: "Coupon verified successfully. Please enter OTP.",
        });
        setIsCouponVerified(true);
        setIsOtpVerified(false);
      }
    } catch (error) {
      console.error("Error verifying coupon:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to verify coupon",
        variant: "destructive",
      });
      setIsCouponVerified(false);
      setIsOtpVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otp = form.getValues("otp");
    if (!otp || !otp.trim()) {
      toast({
        title: "Error",
        description: "Please enter OTP",
        variant: "destructive",
      });
      return;
    }

    setIsVerifyingOtp(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (otp === "1992") {
      setIsOtpVerified(true);
      toast({
        title: "Success",
        description: "OTP verified successfully",
      });
    } else {
      setIsOtpVerified(false);
      toast({
        title: "Error",
        description: "Invalid OTP. Please try again",
        variant: "destructive",
      });
    }

    setIsVerifyingOtp(false);
  };

  const onSubmit = async (data: TransactionFormValues) => {
    // Validate OTP if coupon redeem is checked
    if (data.isCouponRedeem && !isOtpVerified) {
      toast({
        title: "Error",
        description: "Please verify OTP before submitting",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CouponEarnRedeemTransactionRequest = {
        p_invoiceNo: data.invoiceNo,
        p_invoiceAmount: Number(data.invoiceAmount),
        p_mobileNo: data.mobileNo,
        p_isCouponRedeem: data.isCouponRedeem,
        p_CouponCode: data.isCouponRedeem ? data.couponCode || "" : "",
        p_OTP: "",
        p_OutletId: 0,
      };

      const response = await CouponApi.couponEarnRedeemTransaction(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
        // Reset verification states on error to enable fields
        setIsCouponVerified(false);
        setIsOtpVerified(false);
      } else {
        toast({
          title: "Success",
          description: response.message || "Transaction processed successfully",
        });
        form.reset();
        setIsCouponVerified(false);
        setIsOtpVerified(false);
      }
    } catch (error) {
      console.error("Error processing transaction:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to process transaction",
        variant: "destructive",
      });
      // Reset verification states on error to enable fields
      setIsCouponVerified(false);
      setIsOtpVerified(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setIsCouponVerified(false);
    setIsOtpVerified(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transaction</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Invoice Number */}
            <FormField
              control={form.control}
              name="invoiceNo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice Number *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter invoice number"
                      {...field}
                      disabled={isCouponRedeem && isCouponVerified}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the unique invoice number for this transaction
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Invoice Amount */}
            <FormField
              control={form.control}
              name="invoiceAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice Amount *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter invoice amount"
                      {...field}
                      disabled={isCouponRedeem && isCouponVerified}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the total amount of the invoice
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Mobile Number */}
            <FormField
              control={form.control}
              name="mobileNo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number *</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter mobile number"
                      {...field}
                      disabled={isCouponRedeem && isCouponVerified}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers (removes all non-digit characters including negative signs)
                        const numericValue = value.replace(/\D/g, "");
                        // Ensure no negative values
                        if (numericValue && Number(numericValue) >= 0) {
                          field.onChange(numericValue);
                        } else if (numericValue === "") {
                          field.onChange("");
                        }
                      }}
                      maxLength={15}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the customer's mobile number associated with this
                    transaction
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Redeem Coupon Checkbox */}
            <FormField
              control={form.control}
              name="isCouponRedeem"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="rounded-none"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Redeem Coupon</FormLabel>
                    <FormDescription>
                      Check this box if you want to redeem a coupon for this
                      transaction
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Coupon Code Field - Show when checkbox is checked */}
            {isCouponRedeem && (
              <FormField
                control={form.control}
                name="couponCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coupon Code</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          placeholder="Enter coupon code"
                          {...field}
                          className="flex-1"
                        />
                      </FormControl>
                      <Button
                        type="button"
                        onClick={handleVerifyCoupon}
                        disabled={
                          isVerifying || !field.value || isCouponVerified
                        }
                        variant="outline"
                      >
                        {isVerifying
                          ? "Verifying..."
                          : isCouponVerified
                          ? "Verified"
                          : "Verify"}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* OTP Field - Show when coupon is verified */}
            {isCouponRedeem && isCouponVerified && (
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verify OTP</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          placeholder="Enter OTP"
                          {...field}
                          className="flex-1"
                          type="password"
                        />
                      </FormControl>
                      <Button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={
                          !field.value || isOtpVerified || isVerifyingOtp
                        }
                        variant="outline"
                      >
                        {isVerifyingOtp
                          ? "Verifying..."
                          : isOtpVerified
                          ? "Verified"
                          : "Verify OTP"}
                      </Button>
                    </div>
                    {isOtpVerified && (
                      <p className="text-sm text-green-600">
                        OTP verified successfully
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Submit and Cancel Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || (isCouponRedeem && !isOtpVerified)}
              >
                {isSubmitting ? "Processing..." : "Submit"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
